import { ApplicationRef, ComponentRef, DebugElement, EnvironmentInjector, Injectable, createComponent, ɵgetDebugNode as getDebugNode, inject } from '@angular/core';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatSelectChipItem, MatSelectChipsComponent } from '../components/mat-select-chips/mat-select-chips.component';

@Injectable({ providedIn: 'root' })
export class MatSelectChipsService {
  private static readonly OPT_OUT_CLASS = 'no-chips';
  private static readonly HOST_CLASS = 'app-select-chips';
  private static readonly SKIP_ANCESTORS = '.mat-select-button, .account-line-mat-select';

  private appRef = inject(ApplicationRef);
  private injector = inject(EnvironmentInjector);
  private observer?: MutationObserver;
  private readonly processed = new WeakSet<Element>();

  init(): void {
    if (typeof document === 'undefined' || this.observer) return;

    this.processNode(document.body);

    this.observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node instanceof Element) this.processNode(node);
        });
      }
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  private processNode(root: Element): void {
    if (root.matches?.('mat-select')) this.attach(root as HTMLElement);
    root.querySelectorAll?.<HTMLElement>('mat-select').forEach((el) => this.attach(el));
  }

  private attach(el: HTMLElement, retry = true): void {
    if (this.processed.has(el) || !el.isConnected) return;

    const select = this.resolveSelect(el);
    if (!select) {
      if (retry) queueMicrotask(() => this.attach(el, false));
      return;
    }
    this.processed.add(el);

    if (!select.multiple || this.isSkipped(el, select)) return;

    const valueEl = el.querySelector<HTMLElement>('.mat-mdc-select-value');
    if (!valueEl) return;

    const ref: ComponentRef<MatSelectChipsComponent> = createComponent(MatSelectChipsComponent, { environmentInjector: this.injector });
    const hostEl = ref.location.nativeElement as HTMLElement;
    this.appRef.attachView(ref.hostView);
    valueEl.appendChild(hostEl);
    el.classList.add(MatSelectChipsService.HOST_CLASS);

    const rowsAttr = el.getAttribute('data-chips-rows');
    if (rowsAttr !== null && !Number.isNaN(Number(rowsAttr))) ref.setInput('maxRows', Number(rowsAttr));

    let items: MatSelectChipItem[] = [];
    let disabled: boolean | undefined;

    const sync = (): void => {
      const next = this.readItems(select);
      if (!this.sameItems(items, next)) {
        items = next;
        ref.setInput('items', items);
      }
      if (select.disabled !== disabled) {
        disabled = select.disabled;
        ref.setInput('disabled', disabled);
      }
    };

    const valueObserver = new MutationObserver((mutations) => {
      if (mutations.some((m) => !hostEl.contains(m.target))) sync();
    });
    valueObserver.observe(valueEl, { childList: true, subtree: true, characterData: true });

    const subscription = select.stateChanges.subscribe({
      next: sync,
      complete: () => {
        valueObserver.disconnect();
        this.appRef.detachView(ref.hostView);
        ref.destroy();
        hostEl.remove();
        el.classList.remove(MatSelectChipsService.HOST_CLASS);
      },
    });
    ref.onDestroy(() => subscription.unsubscribe());

    sync();
  }

  private resolveSelect(el: HTMLElement): MatSelect | null {
    try {
      const component = (getDebugNode(el) as DebugElement | null)?.componentInstance;
      return component instanceof MatSelect ? component : null;
    } catch {
      return null;
    }
  }

  private isSkipped(el: HTMLElement, select: MatSelect): boolean {
    if (select.customTrigger) return true;
    if (el.classList.contains(MatSelectChipsService.OPT_OUT_CLASS)) return true;
    if (el.closest(MatSelectChipsService.SKIP_ANCESTORS)) return true;
    return this.hasPanelClass(select, MatSelectChipsService.OPT_OUT_CLASS);
  }

  private hasPanelClass(select: MatSelect, cls: string): boolean {
    const panelClass = select.panelClass;
    if (!panelClass) return false;
    if (typeof panelClass === 'string') return panelClass.split(/\s+/).includes(cls);
    if (Array.isArray(panelClass)) return panelClass.includes(cls);
    if (panelClass instanceof Set) return panelClass.has(cls);
    return !!(panelClass as Record<string, unknown>)[cls];
  }

  private readItems(select: MatSelect): MatSelectChipItem[] {
    const selected = (select.selected ?? []) as MatOption[];
    return selected.map((option) => ({ option, label: option.viewValue, disabled: option.disabled }));
  }

  private sameItems(a: MatSelectChipItem[], b: MatSelectChipItem[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((item, i) => item.option === b[i].option && item.label === b[i].label && item.disabled === b[i].disabled);
  }
}
